/**
 * AP-41 (5.1) — **Verträge in der Betreiber-Konsole.** Dokumente mit Geltung, Fassungen als PDF
 * (Entwurf → veröffentlichen), Frist verlängern, Mandanten zuordnen, Erinnerungen, und der Stand
 * jedes Mandanten mit Nachweis.
 *
 * Eine veröffentlichte Fassung ist unveränderlich — neuer Inhalt heißt neue Fassung (V-7).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilePlus2, FileSignature, FileText, Plus } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { Button } from '@/components/ui/button'
import { StandMarke } from '@/components/vertraege/Vertraege'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige, frage } from '@/lib/dialoge'
import { downloadFile } from '@/lib/download'
import { fmtDateLong, fmtDateTime } from '@/lib/format-date'
import { fieldLabel, formCard, inputBase, pageWide, selectBase, textareaBase } from '@/lib/form-styles'
import type { VertragsGeltung } from '@/lib/vertraege'
import { vertraegeKonsoleApi, type KonsolenDokument, type KonsolenFassung, type KonsolenStandZeile, type NeueFassung } from '../api/vertraege.api'

/** `2026-10-31` aus einem Datumsfeld → Ende dieses Tages in Ortszeit, als ISO. */
const tagesende = (tag: string) => (tag ? new Date(`${tag}T23:59:59`).toISOString() : '')
const heutePlus = (tage: number) => new Date(Date.now() + tage * 86_400_000).toISOString().slice(0, 10)

type Meldung = { art: 'ok' | 'fehler'; text: string } | null

function DokumentAnlegen({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<VertragsGeltung>('ALL')
  const [fehler, setFehler] = useState<string | null>(null)
  const anlegen = async () => {
    try {
      await vertraegeKonsoleApi.anlegen({ title: title.trim(), description: description.trim() || undefined, scope })
      onFertig()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('contracts.konsole.dokumentAnlegen')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={title.trim().length < 2} onClick={() => void anlegen()}>{t('common.create')}</Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.titel')}</span>
          <input className={inputBase} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('contracts.konsole.titelBeispiel')} autoFocus />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.geltung')}</span>
          <select className={selectBase} value={scope} onChange={(e) => setScope(e.target.value as VertragsGeltung)}>
            {(['ALL', 'AI', 'SELECTED'] as const).map((s) => (
              <option key={s} value={s}>{t(`contracts.geltung.${s}`)}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.beschreibung')}</span>
          <textarea className={textareaBase} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        {fehler && <p className="text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}

function FassungAnlegen({ dok, onClose, onFertig }: { dok: KonsolenDokument; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const [datei, setDatei] = useState<File | null>(null)
  const [label, setLabel] = useState('')
  const [mode, setMode] = useState<'ACCEPT' | 'NOTICE'>('ACCEPT')
  const [changeSummary, setChangeSummary] = useState('')
  const [frist, setFrist] = useState(heutePlus(30))
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const anlegen = async () => {
    if (!datei) return
    setLaeuft(true)
    setFehler(null)
    const daten: NeueFassung = {
      label: label.trim(),
      mode,
      changeSummary: changeSummary.trim(),
      acceptBy: mode === 'ACCEPT' ? tagesende(frist) : '',
      objectionUntil: mode === 'NOTICE' ? tagesende(frist) : '',
    }
    try {
      await vertraegeKonsoleApi.fassung(dok.id, datei, daten)
      onFertig()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('contracts.konsole.fassungAnlegen', { titel: dok.title })}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!datei || laeuft} onClick={() => void anlegen()}>{t('common.create')}</Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.pdf')}</span>
          <input type="file" accept="application/pdf,.pdf" onChange={(e) => setDatei(e.target.files?.[0] ?? null)} />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.bezeichnung')}</span>
          <input className={inputBase} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('contracts.konsole.bezeichnungBeispiel')} />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.konsole.art')}</span>
          <select className={selectBase} value={mode} onChange={(e) => setMode(e.target.value as 'ACCEPT' | 'NOTICE')}>
            <option value="ACCEPT">{t('contracts.konsole.artAnnahme')}</option>
            <option value="NOTICE">{t('contracts.konsole.artMitteilung')}</option>
          </select>
        </label>
        <label className="block">
          <span className={fieldLabel}>{mode === 'ACCEPT' ? t('contracts.konsole.annehmenBis') : t('contracts.konsole.widerspruchBis')}</span>
          <input type="date" className={inputBase} value={frist} onChange={(e) => setFrist(e.target.value)} />
          <span className="mt-1 block text-xs text-muted-foreground">{t('contracts.konsole.fristHinweis')}</span>
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.wasIstNeu')}</span>
          <textarea className={textareaBase} rows={3} value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} />
        </label>
        {fehler && <p className="text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}

function Zuordnen({ dok, mandanten, onClose, onFertig }: { dok: KonsolenDokument; mandanten: KonsolenStandZeile[]; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const [gewaehlt, setGewaehlt] = useState(new Set(dok.tenantIds))
  const [fehler, setFehler] = useState<string | null>(null)
  const uebernehmen = async () => {
    try {
      await vertraegeKonsoleApi.zuordnen(dok.id, [...gewaehlt])
      onFertig()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('contracts.konsole.zuordnenTitel', { titel: dok.title })}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => void uebernehmen()}>{t('contracts.konsole.uebernehmen')}</Button>
        </div>
      }
    >
      <div className="max-h-80 space-y-1 overflow-y-auto text-sm">
        {mandanten.map((m) => (
          <label key={m.tenantId} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={gewaehlt.has(m.tenantId)}
              onChange={(e) => {
                const neu = new Set(gewaehlt)
                if (e.target.checked) neu.add(m.tenantId)
                else neu.delete(m.tenantId)
                setGewaehlt(neu)
              }}
            />
            {m.name}
          </label>
        ))}
        {fehler && <p className="text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}

export default function OperatorVertraegePage() {
  const { t } = useTranslation()
  const [dokumente, setDokumente] = useState<KonsolenDokument[] | null>(null)
  const [stand, setStand] = useState<{ dokumente: { id: string; title: string }[]; zeilen: KonsolenStandZeile[] } | null>(null)
  const [meldung, setMeldung] = useState<Meldung>(null)
  const [dialog, setDialog] = useState<null | { art: 'dokument' } | { art: 'fassung' | 'zuordnen'; dok: KonsolenDokument }>(null)

  const laden = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([vertraegeKonsoleApi.dokumente(), vertraegeKonsoleApi.stand()])
      setDokumente(d)
      setStand(s)
    } catch (e) {
      setMeldung({ art: 'fehler', text: apiErrorText(e, t('common.error')) })
    }
  }, [t])
  useEffect(() => {
    void laden()
  }, [laden])

  const handle = async (fn: () => Promise<unknown>, ok?: string) => {
    setMeldung(null)
    try {
      await fn()
      if (ok) setMeldung({ art: 'ok', text: ok })
      await laden()
    } catch (e) {
      setMeldung({ art: 'fehler', text: apiErrorText(e, t('common.error')) })
    }
  }

  const veroeffentlichen = async (dok: KonsolenDokument, v: KonsolenFassung) => {
    if (!(await bestaetige(t('contracts.konsole.veroeffentlichenFrage', { titel: dok.title, label: v.label }), { ok: t('contracts.konsole.veroeffentlichen') }))) return
    await handle(() => vertraegeKonsoleApi.veroeffentlichen(v.id), t('contracts.konsole.veroeffentlicht'))
  }
  const verlaengern = async (v: KonsolenFassung) => {
    const bisher = v.mode === 'ACCEPT' ? v.acceptBy : v.objectionUntil
    const tag = (await frage(t('contracts.konsole.verlaengernFrage'), (bisher ?? '').slice(0, 10), { pflicht: true, ok: t('contracts.konsole.verlaengern') }))?.trim()
    if (!tag) return
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tag)) {
      setMeldung({ art: 'fehler', text: t('contracts.konsole.datumFormat') })
      return
    }
    await handle(() => vertraegeKonsoleApi.verlaengern(v.id, v.mode === 'ACCEPT' ? { acceptBy: tagesende(tag) } : { objectionUntil: tagesende(tag) }), t('contracts.konsole.verlaengert'))
  }
  const pdf = async (v: KonsolenFassung) => downloadFile(await vertraegeKonsoleApi.pdf(v.id), v.fileName, 'application/pdf')

  const mandanten = stand?.zeilen ?? []
  const titelJe = useMemo(() => new Map((stand?.dokumente ?? []).map((d) => [d.id, d.title])), [stand])

  return (
    <div className={pageWide}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <FileSignature size={22} /> {t('contracts.konsole.titelSeite')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('contracts.konsole.untertitel')}</p>
        </div>
        <Button onClick={() => setDialog({ art: 'dokument' })}>
          <Plus size={16} className="mr-1.5" /> {t('contracts.konsole.dokumentAnlegen')}
        </Button>
      </div>

      {meldung && (
        <div className={`rounded-md px-4 py-3 text-sm ${meldung.art === 'ok' ? 'border border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400' : 'border border-destructive/30 bg-destructive/10 text-destructive'}`}>
          {meldung.text}
        </div>
      )}

      {dokumente?.length === 0 && <p className="text-sm text-muted-foreground">{t('contracts.konsole.keine')}</p>}

      {dokumente?.map((dok) => (
        <section key={dok.id} className={formCard}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="font-semibold text-foreground">{dok.title}</h2>
              <p className="text-xs text-muted-foreground">
                {t(`contracts.geltung.${dok.scope}`)}
                {dok.scope === 'SELECTED' && ` · ${t('contracts.konsole.zugeordnet', { anzahl: dok.tenantIds.length })}`}
                {' · '}
                {t('contracts.konsole.summe', dok.summe)}
              </p>
              {dok.description && <p className="mt-1 text-sm text-muted-foreground">{dok.description}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setDialog({ art: 'fassung', dok })}>
                <FilePlus2 size={14} className="mr-1.5" /> {t('contracts.konsole.neueFassung')}
              </Button>
              {dok.scope === 'SELECTED' && (
                <Button size="sm" variant="outline" onClick={() => setDialog({ art: 'zuordnen', dok })}>
                  {t('contracts.konsole.zuordnen')}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  if (await bestaetige(t('contracts.konsole.entfernenFrage', { titel: dok.title }), { ok: t('common.delete'), gefaehrlich: true })) {
                    await handle(() => vertraegeKonsoleApi.entfernen(dok.id))
                  }
                }}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>

          {dok.versions.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">{t('contracts.fassung')}</th>
                    <th className="py-2 pr-3">{t('contracts.konsole.art')}</th>
                    <th className="py-2 pr-3">{t('contracts.konsole.status')}</th>
                    <th className="py-2 pr-3">{t('contracts.konsole.frist')}</th>
                    <th className="py-2 pr-3">{t('contracts.pruefsumme')}</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {dok.versions.map((v) => {
                    const frist = v.mode === 'ACCEPT' ? v.acceptBy : v.objectionUntil
                    return (
                      <tr key={v.id} className="border-b border-border/60 align-top">
                        <td className="py-2 pr-3 text-foreground">
                          {v.label}
                          {v.changeSummary && <div className="max-w-xs text-xs text-muted-foreground">{v.changeSummary}</div>}
                        </td>
                        <td className="py-2 pr-3">{v.mode === 'ACCEPT' ? t('contracts.konsole.artAnnahme') : t('contracts.konsole.artMitteilung')}</td>
                        <td className="py-2 pr-3">
                          {v.status === 'DRAFT' ? t('contracts.konsole.entwurf') : t('contracts.veroeffentlicht', { datum: fmtDateLong(v.publishedAt!) })}
                          {v.consents > 0 && <div className="text-xs text-muted-foreground">{t('contracts.konsole.zustimmungen', { anzahl: v.consents })}</div>}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">{frist ? fmtDateLong(frist) : '—'}</td>
                        <td className="py-2 pr-3"><code className="text-xs">{v.contentHash.slice(0, 12)}…</code></td>
                        <td className="py-2 text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => void pdf(v)} title={t('contracts.pdfAnsehen')}>
                              <FileText size={14} />
                            </Button>
                            {v.status === 'DRAFT' ? (
                              <>
                                <Button size="sm" onClick={() => void veroeffentlichen(dok, v)}>{t('contracts.konsole.veroeffentlichen')}</Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    if (await bestaetige(t('contracts.konsole.fassungLoeschenFrage'), { ok: t('common.delete'), gefaehrlich: true })) await handle(() => vertraegeKonsoleApi.fassungLoeschen(v.id))
                                  }}
                                >
                                  {t('common.delete')}
                                </Button>
                              </>
                            ) : (
                              <>
                                {frist && <Button size="sm" variant="outline" onClick={() => void verlaengern(v)}>{t('contracts.konsole.verlaengern')}</Button>}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => void handle(async () => {
                                    const r = await vertraegeKonsoleApi.erinnern(v.id)
                                    setMeldung({ art: 'ok', text: t('contracts.konsole.erinnert', { anzahl: r.versendetAn }) })
                                  })}
                                >
                                  {t('contracts.konsole.erinnern')}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      {stand && stand.dokumente.length > 0 && (
        <section className={formCard}>
          <h2 className="mb-3 font-semibold text-foreground">{t('contracts.konsole.standJeMandant')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">{t('contracts.konsole.mandant')}</th>
                  {stand.dokumente.map((d) => (
                    <th key={d.id} className="py-2 pr-3">{d.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mandanten.map((m) => (
                  <tr key={m.tenantId} className="border-b border-border/60 align-top">
                    <td className="py-2 pr-3 text-foreground">
                      {m.name}
                      {/* Nachtrag 1: auf wen der Betreiber wartet — samt dem, was den Eintrag noch nicht gelten lässt. */}
                      {m.annahmeDurch.length > 0 && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t('contracts.konsole.annahmeDurch', {
                            namen: m.annahmeDurch
                              .map((p) => (p.aktiv ? p.name : `${p.name} (${t(p.angemeldetGewesen ? 'contracts.konsole.deaktiviert' : 'contracts.konsole.eingeladen')})`))
                              .join(', '),
                          })}
                        </div>
                      )}
                    </td>
                    {stand.dokumente.map((d) => {
                      const zelle = m.dokumente.find((x) => x.documentId === d.id)
                      if (!zelle) return <td key={d.id} className="py-2 pr-3 text-muted-foreground">—</td>
                      const z = zelle.zustimmung
                      return (
                        <td key={d.id} className="py-2 pr-3">
                          <StandMarke stand={zelle.stand} frist={zelle.frist} />
                          {z && (
                            <div className="mt-1 text-xs text-muted-foreground" title={`${z.personEmail} · ${z.ipAddress ?? ''} · ${z.contentHash}`}>
                              {z.personName}
                              {z.personRole && `, ${z.personRole}`} · {fmtDateTime(z.createdAt)}
                              {z.reason && <div className="text-foreground">„{z.reason}"</div>}
                            </div>
                          )}
                          {(zelle.stand === 'OFFEN' || zelle.stand === 'UEBERFAELLIG' || zelle.stand === 'MITTEILUNG') && (
                            <button
                              type="button"
                              className="mt-1 block text-xs text-primary hover:opacity-80"
                              onClick={() => void handle(async () => {
                                const r = await vertraegeKonsoleApi.erinnern(zelle.versionId, m.tenantId)
                                setMeldung({ art: 'ok', text: t('contracts.konsole.erinnertMandant', { mandant: m.name, dokument: titelJe.get(d.id), anzahl: r.versendetAn }) })
                              })}
                            >
                              {t('contracts.konsole.erinnern')}
                            </button>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {dialog?.art === 'dokument' && <DokumentAnlegen onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void laden() }} />}
      {dialog?.art === 'fassung' && <FassungAnlegen dok={dialog.dok} onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void laden() }} />}
      {dialog?.art === 'zuordnen' && <Zuordnen dok={dialog.dok} mandanten={mandanten} onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void laden() }} />}
    </div>
  )
}
